const rid = () =>
  typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;

/** AAAA-MM-JJ (fuseau local du navigateur). */
export const todayYmdLocal = () => {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

export const newStaffLine = () => ({
  id: rid(),
  qualificationId: '',
  hourLineId: '',
  headcount: '',
});

/** Catégories fixes — identifiants stables pour la persistance. */
export const FRAIS_GENERALES_CATEGORIES = [
  { id: 'renouvellement_materiel', label: 'Renouvellement du matériel' },
  { id: 'proprete_dechets', label: 'Propreté et Déchets' },
  { id: 'tenues_personnel', label: 'Tenues du personnel' },
  { id: 'controles_qualite', label: 'Contrôles Qualité et Suivi Contractuel' },
  { id: 'autres_frais_exploitation', label: 'Autres frais d’exploitation' },
  { id: 'assurances_impots_taxes', label: 'Assurances, impôts et taxes' },
  { id: 'frais_gestion_remuneration', label: 'Frais de gestion et de rémunération' },
  { id: 'divers', label: 'Divers' },
];

export const newFraisGenerauxLine = () => ({
  id: rid(),
  name: '',
  clientPct: '50',
  prestatairePct: '50',
  amountHt: '',
});

export const defaultFraisGenerauxState = () => ({
  linesByCategory: Object.fromEntries(FRAIS_GENERALES_CATEGORIES.map((c) => [c.id, []])),
});

export const emptyMainOeuvreSlot = () => ({
  surface: '',
  passages: '',
});

export const emptyMainOeuvreOperation = () => ({
  jour: emptyMainOeuvreSlot(),
  nuit: emptyMainOeuvreSlot(),
  weekend: emptyMainOeuvreSlot(),
});

export const defaultMainOeuvreState = () => ({
  inputsByOperationId: {},
});

export const newInvestissementLine = () => ({
  id: rid(),
  name: '',
  details: '',
  client_ht: '',
  prestataire_ht: '',
});

export const defaultInvestissementState = () => ({
  annees_amortissement: '3',
  linesByCategory: {},
});

/** Même logique que l’onglet Paramètres → Opérations (tri et cadences). */
export const normalizeOperationCategories = (rows) =>
  (Array.isArray(rows) ? rows : [])
    .map((c) => ({
      id: c.id,
      label: c.label ?? '',
      sort_order: Number(c.sort_order) || 0,
      operations: (c.operations || [])
        .map((o) => ({
          id: o.id,
          label: o.label ?? '',
          cadence_m2h: parseNum(o.cadence_m2h, 0),
          sort_order: Number(o.sort_order) || 0,
        }))
        .sort((a, b) => a.sort_order - b.sort_order),
    }))
    .sort((a, b) => a.sort_order - b.sort_order);

export const defaultDevisUiState = () => ({
  salaires: {
    staffLines: [newStaffLine()],
    primeObjectif: '400',
    chargesSocialesPct: '32',
    titreRestaurant: '',
    transport: '',
  },
  frais_generaux: defaultFraisGenerauxState(),
  main_oeuvre: defaultMainOeuvreState(),
  investissement: defaultInvestissementState(),
  chiffrage_global: {},
});

export const mergeDevisUiState = (raw) => {
  const d = defaultDevisUiState();
  if (!raw || typeof raw !== 'object') return d;

  const s = raw.salaires && typeof raw.salaires === 'object' ? raw.salaires : {};

  let staffLines;
  if (Array.isArray(s.staffLines) && s.staffLines.length > 0) {
    staffLines = s.staffLines.map((l) => ({
      id: l.id || rid(),
      qualificationId: l.qualificationId ? String(l.qualificationId) : '',
      hourLineId: l.hourLineId ? String(l.hourLineId) : '',
      headcount:
        l.headcount === undefined || l.headcount === null || l.headcount === ''
          ? ''
          : String(l.headcount),
    }));
  } else {
    staffLines = [newStaffLine()];
  }

  const fgDefault = defaultFraisGenerauxState();
  const rawFg = raw.frais_generaux && typeof raw.frais_generaux === 'object' ? raw.frais_generaux : {};
  const rawLines = rawFg.linesByCategory && typeof rawFg.linesByCategory === 'object' ? rawFg.linesByCategory : {};
  const linesByCategory = { ...fgDefault.linesByCategory };
  for (const cat of FRAIS_GENERALES_CATEGORIES) {
    const arr = rawLines[cat.id];
    if (!Array.isArray(arr)) {
      linesByCategory[cat.id] = [];
      continue;
    }
    linesByCategory[cat.id] = arr.map((l) => ({
      id: l.id || rid(),
      name: l.name != null ? String(l.name) : '',
      clientPct:
        l.clientPct != null && String(l.clientPct).trim() !== '' ? String(l.clientPct) : '50',
      prestatairePct:
        l.prestatairePct != null && String(l.prestatairePct).trim() !== ''
          ? String(l.prestatairePct)
          : '50',
      amountHt:
        l.amountHt != null && l.amountHt !== '' ? String(l.amountHt) : '',
    }));
  }

  const rawMo = raw.main_oeuvre && typeof raw.main_oeuvre === 'object' ? raw.main_oeuvre : {};
  const rawMoIn =
    rawMo.inputsByOperationId && typeof rawMo.inputsByOperationId === 'object'
      ? rawMo.inputsByOperationId
      : {};
  const slot = (s) => ({
    surface: s?.surface != null && String(s.surface).trim() !== '' ? String(s.surface) : '',
    passages: s?.passages != null && String(s.passages).trim() !== '' ? String(s.passages) : '',
  });
  const inputsByOperationId = {};
  for (const [opId, v] of Object.entries(rawMoIn)) {
    if (!v || typeof v !== 'object') continue;
    inputsByOperationId[opId] = {
      jour: slot(v.jour),
      nuit: slot(v.nuit),
      weekend: slot(v.weekend),
    };
  }

  const invDef = defaultInvestissementState();
  const rawInv = raw.investissement && typeof raw.investissement === 'object' ? raw.investissement : {};
  const rawInvLines =
    rawInv.linesByCategory && typeof rawInv.linesByCategory === 'object' ? rawInv.linesByCategory : {};
  const invLinesByCategory = {};
  for (const [catId, arr] of Object.entries(rawInvLines)) {
    if (!Array.isArray(arr)) continue;
    invLinesByCategory[catId] = arr.map((l) => ({
      id: l.id || rid(),
      name: l.name != null ? String(l.name) : '',
      details: l.details != null ? String(l.details) : '',
      client_ht: l.client_ht != null && l.client_ht !== '' ? String(l.client_ht) : '',
      prestataire_ht: l.prestataire_ht != null && l.prestataire_ht !== '' ? String(l.prestataire_ht) : '',
    }));
  }
  const anneesAmort =
    rawInv.annees_amortissement != null && String(rawInv.annees_amortissement).trim() !== ''
      ? String(rawInv.annees_amortissement)
      : invDef.annees_amortissement;

  return {
    ...d,
    frais_generaux: { linesByCategory },
    main_oeuvre: { inputsByOperationId },
    investissement: {
      annees_amortissement: anneesAmort,
      linesByCategory: invLinesByCategory,
    },
    chiffrage_global:
      raw.chiffrage_global && typeof raw.chiffrage_global === 'object' ? raw.chiffrage_global : {},
    salaires: {
      ...d.salaires,
      staffLines,
      primeObjectif:
        s.primeObjectif != null && String(s.primeObjectif).trim() !== ''
          ? String(s.primeObjectif)
          : d.salaires.primeObjectif,
      chargesSocialesPct:
        s.chargesSocialesPct != null && String(s.chargesSocialesPct).trim() !== ''
          ? String(s.chargesSocialesPct)
          : d.salaires.chargesSocialesPct,
      titreRestaurant: s.titreRestaurant != null ? String(s.titreRestaurant) : '',
      transport: s.transport != null ? String(s.transport) : '',
    },
  };
};

export const parseNum = (v, fallback = 0) => {
  if (v === '' || v === null || v === undefined) return fallback;
  const n = Number(String(v).replace(',', '.'));
  return Number.isFinite(n) ? n : fallback;
};

/** Durée (h) = surface (m²) ÷ cadence (m²/h). */
export const mainOeuvreDurationHours = (surfaceStr, cadenceM2h) => {
  const s = parseNum(surfaceStr, 0);
  const c = cadenceM2h > 0 ? cadenceM2h : 0;
  if (!(s > 0) || !(c > 0)) return null;
  return s / c;
};

/** Passages/semaine × durée (h) pour un créneau. */
export const mainOeuvreSlotProduct = (surfaceStr, passagesStr, cadenceM2h) => {
  const d = mainOeuvreDurationHours(surfaceStr, cadenceM2h);
  if (d == null) return 0;
  return parseNum(passagesStr, 0) * d;
};

const MO_SLOT_KEYS = ['jour', 'nuit', 'weekend'];

export const mergeMainOeuvreOpInputs = (map, opId) => {
  const empty = emptyMainOeuvreOperation();
  const cur = map?.[opId];
  if (!cur) return empty;
  return {
    jour: { ...empty.jour, ...cur.jour },
    nuit: { ...empty.nuit, ...cur.nuit },
    weekend: { ...empty.weekend, ...cur.weekend },
  };
};

/** Σ (passages × durée h) par semaine et par catégorie d’opération (cohérent avec l’onglet Main d’œuvre). */
export const computeMainOeuvreCategoryWeeklyTotals = (mainOeuvre, operationCategories) => {
  const map = mainOeuvre?.inputsByOperationId || {};
  const cats = Array.isArray(operationCategories) ? operationCategories : [];
  const out = {};
  for (const cat of cats) {
    let sum = 0;
    for (const op of cat.operations || []) {
      const inp = mergeMainOeuvreOpInputs(map, op.id);
      const c = op.cadence_m2h;
      for (const key of MO_SLOT_KEYS) {
        const sl = inp[key];
        sum += mainOeuvreSlotProduct(sl.surface, sl.passages, c);
      }
    }
    out[cat.id] = sum;
  }
  return out;
};

function getSlotsUsedInCategory(cat, mainOeuvre) {
  const map = mainOeuvre?.inputsByOperationId || {};
  const used = new Set();
  for (const op of cat.operations || []) {
    const inp = mergeMainOeuvreOpInputs(map, op.id);
    const c = op.cadence_m2h;
    for (const key of MO_SLOT_KEYS) {
      const sl = inp[key];
      const hasProduct = mainOeuvreSlotProduct(sl.surface, sl.passages, c) > 0;
      const hasSaisie =
        parseNum(sl.surface, 0) > 0 && parseNum(sl.passages, 0) > 0;
      if (hasProduct || hasSaisie) used.add(key);
    }
  }
  return used;
}

/** Surface et passages > 0 sur au moins un créneau (sans exiger cadence ni heures hebdo > 0). */
function categoryHasMoSaisieForStaffLine(cat, mainOeuvre) {
  const map = mainOeuvre?.inputsByOperationId || {};
  for (const op of cat.operations || []) {
    const inp = mergeMainOeuvreOpInputs(map, op.id);
    for (const key of MO_SLOT_KEYS) {
      const sl = inp[key];
      if (parseNum(sl.surface, 0) > 0 && parseNum(sl.passages, 0) > 0) return true;
    }
  }
  return false;
}

function pickHourLineFromSlots(hourLines, usedSlots) {
  if (!hourLines.length) return null;
  const L = (s) => String(s || '').toLowerCase();
  const pickNuit = () => hourLines.find((h) => L(h.type_label).includes('nuit'));
  const pickWeekend = () =>
    hourLines.find(
      (h) =>
        L(h.type_label).includes('week-end') ||
        L(h.type_label).includes('weekend') ||
        L(h.type_label).includes(' week-end') ||
        /\bwe\b/.test(L(h.type_label))
    );
  const pickJour = () => {
    const x = hourLines.find(
      (h) => L(h.type_label).includes('jour') && !L(h.type_label).includes('nuit')
    );
    if (x) return x;
    return hourLines.find((h) => L(h.type_label).includes('jour'));
  };
  if (usedSlots.size === 1) {
    const [only] = [...usedSlots];
    if (only === 'nuit') {
      const x = pickNuit();
      if (x) return x;
    }
    if (only === 'weekend') {
      const x = pickWeekend();
      if (x) return x;
    }
    if (only === 'jour') {
      const y = pickJour();
      if (y) return y;
    }
  }
  if (usedSlots.size > 1) {
    if (usedSlots.has('nuit')) {
      const x = pickNuit();
      if (x) return x;
    }
    if (usedSlots.has('weekend')) {
      const x = pickWeekend();
      if (x) return x;
    }
    if (usedSlots.has('jour')) {
      const y = pickJour();
      if (y) return y;
    }
  }
  return hourLines[0];
}

/** Identifiants de catégories d’opération liées au poste (liste dédoublonnée, ordre conservé). */
export function qualificationOperationCategoryIds(qual) {
  if (!qual || typeof qual !== 'object') return [];
  const raw = qual.operation_category_ids;
  if (Array.isArray(raw)) {
    const out = [];
    for (const x of raw) {
      const s = x != null ? String(x).trim() : '';
      if (s && !out.includes(s)) out.push(s);
    }
    return out;
  }
  const a = qual.operation_category_id != null ? String(qual.operation_category_id).trim() : '';
  const b = qual.operation_category_id_2 != null ? String(qual.operation_category_id_2).trim() : '';
  const out = [];
  if (a) out.push(a);
  if (b && !out.includes(b)) out.push(b);
  return out;
}

/**
 * Si la Main d’œuvre a des heures pour une catégorie sans ligne Salaires (poste lié à cette catégorie),
 * ajoute une ligne avec ce poste, un type d’horaire adapté au créneau saisi, et effectif 1.
 */
export function ensureStaffLinesForMainOeuvre(prev, operationCategories, qualifications) {
  const cats = Array.isArray(operationCategories) ? operationCategories : [];
  const quals = Array.isArray(qualifications) ? qualifications : [];
  const mo = prev?.main_oeuvre;
  const weekly = computeMainOeuvreCategoryWeeklyTotals(mo, cats);
  const nextLines = Array.isArray(prev?.salaires?.staffLines) ? [...prev.salaires.staffLines] : [];
  let changed = false;

  for (const cat of cats) {
    const hasWeekly = (weekly[cat.id] || 0) > 0;
    const hasSaisie = categoryHasMoSaisieForStaffLine(cat, mo);
    if (!hasWeekly && !hasSaisie) continue;

    const hasLine = nextLines.some((line) => {
      const q = quals.find((x) => String(x.id) === String(line.qualificationId));
      if (!q) return false;
      return qualificationOperationCategoryIds(q).some((id) => String(id) === String(cat.id));
    });
    if (hasLine) continue;

    const qual = quals.find((q) =>
      qualificationOperationCategoryIds(q).some((id) => String(id) === String(cat.id))
    );
    if (!qual) continue;

    const hlList = Array.isArray(qual.hour_lines) ? qual.hour_lines : [];
    if (hlList.length === 0) continue;

    const usedSlots = getSlotsUsedInCategory(cat, mo);
    const picked = pickHourLineFromSlots(hlList, usedSlots);
    const hourLineId = picked?.id != null ? String(picked.id) : String(hlList[0].id);

    nextLines.push({
      ...newStaffLine(),
      qualificationId: String(qual.id),
      hourLineId,
      headcount: '1',
    });
    changed = true;
  }

  if (!changed) return null;
  return {
    ...prev,
    salaires: {
      ...prev.salaires,
      staffLines: nextLines,
    },
  };
}

const WEEKS_YEAR_SALAIRE = 52;

/**
 * Salaire brut annuel par ligne d’affectation (même moteur que le total agrégé).
 * @returns {{ lineId: string, postLabel: string, hourTypeLabel: string, headcount: number, brutAnnuel: number }[]}
 */
export const computeSalaireBrutByStaffLine = (
  salaires,
  qualifications,
  mainOeuvre,
  operationCategories,
  weeksYear = WEEKS_YEAR_SALAIRE
) => {
  const weeklyByCat = computeMainOeuvreCategoryWeeklyTotals(mainOeuvre, operationCategories);
  const quals = Array.isArray(qualifications) ? qualifications : [];
  const staffLines = salaires?.staffLines || [];

  const lineBaseByStaffId = {};
  const expandedMetas = [];
  for (const line of staffLines) {
    const q = quals.find((x) => String(x.id) === String(line.qualificationId));
    const catIds = qualificationOperationCategoryIds(q);
    const hcRaw = parseInt(String(line.headcount ?? '').replace(/\s/g, ''), 10);
    const hc = Number.isFinite(hcRaw) && hcRaw > 0 ? hcRaw : 0;
    const hl = Array.isArray(q?.hour_lines)
      ? q.hour_lines.find((h) => String(h.id) === String(line.hourLineId))
      : null;
    const taux = parseNum(hl?.taux_net, 0);
    const postLabel = q ? String(q.label || q.id).trim() : '';
    const hourTypeLabel = (hl?.type_label || '').trim();
    lineBaseByStaffId[line.id] = { hc, taux, postLabel, hourTypeLabel };
    if (catIds.length === 0) {
      expandedMetas.push({ lineId: line.id, catId: '', hc, taux, postLabel, hourTypeLabel });
    } else {
      for (const catId of catIds) {
        expandedMetas.push({ lineId: line.id, catId, hc, taux, postLabel, hourTypeLabel });
      }
    }
  }

  const grouped = {};
  for (const m of expandedMetas) {
    if (!m.catId || m.hc <= 0 || !(m.taux > 0)) continue;
    if (!grouped[m.catId]) grouped[m.catId] = [];
    grouped[m.catId].push(m);
  }

  const brutByLineId = {};
  for (const catId of Object.keys(grouped)) {
    const weekly = weeklyByCat[catId] ?? 0;
    const annualH = weekly * weeksYear;
    if (!(annualH > 0)) continue;
    const rows = grouped[catId];
    const sumHc = rows.reduce((s, r) => s + r.hc, 0);
    if (!(sumHc > 0)) continue;
    for (const r of rows) {
      const shareH = (r.hc / sumHc) * annualH;
      const add = shareH * r.taux;
      brutByLineId[r.lineId] = (brutByLineId[r.lineId] ?? 0) + add;
    }
  }

  return staffLines.map((line) => {
    const b = lineBaseByStaffId[line.id];
    return {
      lineId: line.id,
      postLabel: b?.postLabel || '—',
      hourTypeLabel: b?.hourTypeLabel || '—',
      headcount: b?.hc ?? 0,
      brutAnnuel: brutByLineId[line.id] ?? 0,
    };
  });
};

/**
 * Salaire brut annuel estimé : pour chaque catégorie, heures annuelles = total hebdo (passages×h) × 52 ;
 * répartition entre les lignes d’affectation selon l’effectif ; chaque part × €/h du type d’heure choisi.
 */
export const computeSalaireBrutAnnuelFromMo = (
  salaires,
  qualifications,
  mainOeuvre,
  operationCategories,
  weeksYear = WEEKS_YEAR_SALAIRE
) =>
  computeSalaireBrutByStaffLine(
    salaires,
    qualifications,
    mainOeuvre,
    operationCategories,
    weeksYear
  ).reduce((s, r) => s + r.brutAnnuel, 0);

/**
 * Total annuel frais fixes (€ HT) pour un devis — même logique que l’onglet Résumé.
 */
export const computeDevisTotalAnnuelFraisFixes = (
  rawDevisUiState,
  qualifications,
  operationCategories
) => {
  const state = mergeDevisUiState(rawDevisUiState);
  const salaires = state.salaires;
  const mainOeuvre = state.main_oeuvre;
  const fraisGeneraux = state.frais_generaux;
  const investissement = state.investissement;

  const salaireBrutAnnuel = computeSalaireBrutAnnuelFromMo(
    salaires,
    qualifications,
    mainOeuvre,
    operationCategories
  );
  const prime = parseNum(salaires.primeObjectif, 400);
  const pctCharges = parseNum(salaires.chargesSocialesPct, 32);
  const totalChargesSociales = (salaireBrutAnnuel + prime) * (pctCharges / 100);
  const tr = parseNum(salaires.titreRestaurant, 0);
  const transp = parseNum(salaires.transport, 0);
  const totalAvantagesTrTransport = tr + transp;
  const totalPersonnelAnnuelHt =
    salaireBrutAnnuel + totalChargesSociales + totalAvantagesTrTransport;

  const linesByCategoryInv = investissement?.linesByCategory || {};
  let tp = 0;
  for (const lines of Object.values(linesByCategoryInv)) {
    if (!Array.isArray(lines)) continue;
    for (const l of lines) {
      tp += parseNum(l.prestataire_ht, 0);
    }
  }
  const nAnnees = parseNum(investissement?.annees_amortissement ?? '3', 0);
  const amortAnnuelHt = nAnnees > 0 ? tp / nAnnees : 0;

  const linesByCategoryFg = fraisGeneraux?.linesByCategory || {};
  let totalFraisGenerauxHt = 0;
  for (const cat of FRAIS_GENERALES_CATEGORIES) {
    const lines = linesByCategoryFg[cat.id] || [];
    totalFraisGenerauxHt += lines.reduce((s, l) => s + parseNum(l.amountHt, 0), 0);
  }

  return totalPersonnelAnnuelHt + amortAnnuelHt + totalFraisGenerauxHt;
};

/** Même logique que backend/chiffrage_pptx_export (nom export PowerPoint). */
export const buildOffrePptxFilename = ({
  clientCompany = '',
  quoteTitle = '',
  quoteDate = '',
  quoteId = '',
} = {}) => {
  const DEFAULT_TITLE = 'Nouveau devis';
  let base = String(clientCompany || '').trim();
  if (!base) {
    const t = String(quoteTitle || '').trim();
    if (t && t !== DEFAULT_TITLE) base = t;
  }
  if (!base) {
    const qid = String(quoteId || '').replace(/[^a-zA-Z0-9]+/g, '').slice(0, 12);
    base = qid || String(quoteTitle || '').trim() || 'devis';
  }
  base = base.replace(/[^\p{L}\p{N}\s_-]/gu, '').replace(/\s+/g, '_').replace(/^_+|_+$/g, '');
  base = base.slice(0, 55) || 'devis';
  const dm = String(quoteDate || '').trim().match(/^(\d{4}-\d{2}-\d{2})/);
  let d;
  if (dm) d = dm[1];
  else {
    const now = new Date();
    d = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(
      now.getDate()
    ).padStart(2, '0')}`;
  }
  return `OFFRE_${base}_${d}.pptx`;
};
